import {
	type Dtos,
	type FinancialPeriod,
	FinancialPeriodPreparationMethod,
	type ISODate,
	PeriodType,
	StatementType,
} from "@inrev/common";

type StatementWithView = {
	view?: FinancialPeriod.BalanceSheetView | FinancialPeriod.IncomeStatementView;
};

const statementSlotKey = (
	date: ISODate,
	typeLabel: string,
	method: FinancialPeriodPreparationMethod,
) => `${date}|${typeLabel}|${method}`;

const countStatementSlots = <TStatement extends StatementWithView>(
	statements: TStatement[],
	getSlotKey: (statement: TStatement) => string,
): Map<string, number> => {
	const counts = new Map<string, number>();
	for (const statement of statements) {
		if (!statement.view) continue;
		const key = getSlotKey(statement);
		counts.set(key, (counts.get(key) ?? 0) + 1);
	}
	return counts;
};

export const balanceSheetTypeLabel = (statementType: StatementType) =>
	statementType === StatementType.Fye ? PeriodType.displayName(PeriodType.Fye) : "Interim";

export const incomeStatementTypeLabel = (periodType: PeriodType) =>
	PeriodType.displayName(periodType);

export const balanceSheetSlotKey = (balanceSheet: Dtos.Admin.BalanceSheet.Get.Response) =>
	statementSlotKey(
		balanceSheet.statementDate,
		balanceSheetTypeLabel(balanceSheet.statementType),
		balanceSheet.preparationMethod,
	);

export const incomeStatementSlotKey = (incomeStatement: Dtos.Admin.IncomeStatement.Get.Response) =>
	statementSlotKey(
		incomeStatement.periodEndDate,
		incomeStatementTypeLabel(incomeStatement.periodType),
		incomeStatement.preparationMethod,
	);

export const balanceSheetSlotCounts = (
	balanceSheets: Dtos.Admin.BalanceSheet.Get.Response[],
): Map<string, number> => countStatementSlots(balanceSheets, balanceSheetSlotKey);

export const incomeStatementSlotCounts = (
	incomeStatements: Dtos.Admin.IncomeStatement.Get.Response[],
): Map<string, number> => countStatementSlots(incomeStatements, incomeStatementSlotKey);
